import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Checkbox, ColorPicker, InputNumber, Select, Space, Typography, message } from 'antd';
import { ExperimentOutlined, ReloadOutlined, UploadOutlined, ExpandOutlined, ExportOutlined } from '@ant-design/icons';
import { ingestionStore } from '../store/IngestionStore';
import coinPricing from '@designwithfriends/common/economy/coinPricing.json';
import { ingestionApi } from '../ingestionApi';
import { sortedVariants, variationLabel } from '../ingestionHelpers';
import { CategoriesProxy } from '../../services/catalogServiceAPI/categoriesProxy';
import { FacetVocabProxy } from '../../services/catalogServiceAPI/facetVocabProxy';
import { WeightedFacetEditor, vocabOptions } from '../../common/weightedFacetEditor';
import { ALLOWED_TYPES, CURTAIN_COLOR_FIELDS, NO_IMAGE_ITEM_TYPES, PRICE_UNITS, SIZE_ITEM_TYPES, TEXTURE_ITEM_TYPES, defaultResizeAxes } from './extractFields';
import { VariationImagePanel } from './VariationImagePanel';
import { ExternalDataView } from './ExternalDataView';
import { CropDialog } from './CropDialog';
import { ReplaceImageDialog } from './ReplaceImageDialog';
import { catalogImageUrl } from '../../images';

// The pipeline prices unlockables on this curve at extraction; the form mirrors it for the fallbacks.
const coinPriceFor = (retail) =>
    retail != null && Number.isFinite(retail) && retail > 0
        ? Math.round(coinPricing.base + coinPricing.factor * Math.pow(retail, coinPricing.exponent))
        : null;

const { Text } = Typography;

const stripHash = (hex) => (hex || '').replace(/^#/, '');

// Size axes are single numbers; tolerate legacy {min,max} objects.
const axisNumber = (v) => (v && typeof v === 'object' ? (v.max ?? v.min ?? null) : (v ?? null));
const coerceSize = (size) => {
    if (!size || typeof size !== 'object') return {};
    const out = {};
    ['width', 'height', 'depth'].forEach((axis) => {
        const n = axisNumber(size[axis]);
        if (n != null) out[axis] = n;
    });
    return out;
};

// A zero-filled size (LLMs emit 0 for axes the listing doesn't state) is not a
// usable size — treat it as missing. Hand-authored ResizableConfigs pass.
const hasUsableSize = (size) => {
    if (!size || typeof size !== 'object') return false;
    if ('sizes' in size) return true;
    return Object.values(coerceSize(size)).some((n) => n > 0);
};

const firstWith = (variants, key) => {
    for (const v of variants || []) {
        const val = v.extracted?.[key];
        if (val != null && !(Array.isArray(val) && val.length === 0)) return val;
    }
    return undefined;
};


const TIER_OPTIONS = [
    { value: 'free', label: 'Free' },
    { value: 'premium', label: 'Premium' },
    { value: 'unlockable', label: 'Unlockable' },
];

export const Step3Extract = observer(({ item }) => {
    const isTexture = TEXTURE_ITEM_TYPES.includes(item.itemType);
    const isRug = item.itemType === 'Rug';
    // A flat rug is a client-generated plane sized by Width/Height (like a wall
    // print); a complex rug goes through the model path (published as FloorItem).
    const isFlatRug = isRug && item.rugClass !== 'complex';
    const supportsSize = SIZE_ITEM_TYPES.includes(item.itemType) || (isRug && !isFlatRug);
    // The Opus builder models the item at its listing size and the model is not
    // resized in the editor, so there are no resize axes to pick (the size itself
    // still matters: it is what the builder is told to match).
    const picksResizeAxes = supportsSize && item.modelEngine !== 'opus';
    const defaultAxes = defaultResizeAxes(item.itemType);
    const needsImage = !NO_IMAGE_ITEM_TYPES.includes(item.itemType);
    const isFloorItem = item.itemType === 'FloorItem';
    // A wall print is a framed one-off, not a tiling texture: no "can be dropped
    // on", specular, or repeat; its Width/Height come from the size variation.
    const isWallPrint = item.itemType === 'WallPrint';
    // A curtain swatch dresses the editor's template panels: no "can be dropped
    // on" / repeat; instead the panel material (opacity + colors) and pattern repeat.
    const isCurtain = item.itemType === 'Curtain';
    // A pillow's cover is a one-off image stretched over the editor's shared
    // pillow template: no "can be dropped on" / specular / repeat; its
    // Width/Height (from the size variation) are the fixed size it is built at.
    const isPillow = item.itemType === 'Pillow';

    // Access tier is per primary variation; one card/group per primary variation value.
    const primaryType = (item.variationTypes || []).find((vt) => vt.isPrimary);
    const primaryVariations = primaryType?.variations?.length ? primaryType.variations : null;
    const tierGroups = primaryVariations
        ? primaryVariations.map((pv) => ({ key: pv.id, label: pv.title }))
        : [{ key: '__single__', label: 'All variants' }];
    const primaryVarKey = (variant) => {
        if (!primaryVariations) return '__single__';
        return (variant.variationIds || []).find((id) => primaryVariations.some((pv) => pv.id === id)) || '__single__';
    };

    const [categories, setCategories] = useState([]);
    const [facetVocab, setFacetVocab] = useState(null);
    const [itemForm, setItemForm] = useState({ categoryIds: [], priceUnit: null, canBeContained: false });
    const [tierForms, setTierForms] = useState({});
    const [forms, setForms] = useState({});
    const [saving, setSaving] = useState(false);
    const [cutoutBusy, setCutoutBusy] = useState(null);
    const [cropVariantId, setCropVariantId] = useState(null);
    const [replaceVariantId, setReplaceVariantId] = useState(null);
    // True once the admin edits any field; blocks the auto re-seed below from
    // clobbering unsaved edits when a background refresh changes the item.
    const dirtyRef = useRef(false);
    const seededItemIdRef = useRef(null);

    // Re-seed the forms whenever the item's extracted data changes (e.g. after a
    // re-extraction or refresh), not only when switching items — otherwise the
    // form keeps stale values since item.id is unchanged.
    const extractedSignature = JSON.stringify((item.variants || []).map((v) => v.extracted || null));

    useEffect(() => {
        new CategoriesProxy().list().then(setCategories).catch(() => setCategories([]));
        new FacetVocabProxy().list().then(setFacetVocab).catch(() => setFacetVocab(null));
    }, []);

    useEffect(() => {
        const itemChanged = seededItemIdRef.current !== item.id;
        seededItemIdRef.current = item.id;
        // Switching items always re-seeds (and clears the dirty flag). A same-item
        // data change (background refresh) re-seeds only if there are no unsaved edits.
        if (itemChanged) {
            dirtyRef.current = false;
        } else if (dirtyRef.current) {
            return;
        }

        setItemForm({
            categoryIds: firstWith(item.variants, 'categoryIds') || [],
            priceUnit: firstWith(item.variants, 'priceUnit') ?? null,
            canBeContained: firstWith(item.variants, 'canBeContained') ?? item.itemType === 'FloorItem',
        });
        // Per primary-variation tier: read from any variant in that group.
        const tiers = {};
        tierGroups.forEach((g) => {
            const v = (item.variants || []).find((variant) => primaryVarKey(variant) === g.key);
            const scrapedPrice =
                v?.extracted?.scrapedPrice ?? v?.extracted?.externalData?.currentPrice ?? null;
            // Coins are whole numbers — match the rounded value the backend persists.
            const roundedPrice = scrapedPrice != null ? Math.round(scrapedPrice) : null;
            const curvedPrice = coinPriceFor(scrapedPrice);
            tiers[g.key] = {
                accessTier: v?.extracted?.accessTier,
                // Prefilled from the scraped price, but a saved (possibly hand-edited)
                // coin price wins so an admin override survives reloads.
                coinPrice:
                    v?.extracted?.coinPrice ??
                    (v?.extracted?.accessTier === 'unlockable' ? curvedPrice : null),
                scrapedPrice: roundedPrice,
                // Tags belong to the (primary) variation — searched in the catalog.
                tags: v?.extracted?.tags || [],
                styles: v?.extracted?.facets?.styles || [],
                roomTypes: v?.extracted?.facets?.roomTypes || [],
                materials: v?.extracted?.facets?.materials || [],
                colors: v?.extracted?.facets?.colors || [],
            };
        });
        setTierForms(tiers);
        const next = {};
        (item.variants || []).forEach((v) => {
            const e = v.extracted || {};
            next[v.id] = {
                size: coerceSize(e.size),
                resizeAxes: e.resizeAxes || defaultAxes,
                allowedType: e.allowedType || undefined,
                specularColor: e.specularColor || '',
                uSize: e.uSize ?? null,
                vSize: e.vSize ?? null,
                repeat: e.repeat ?? false,
                rugShape: e.rugShape || 'rect',
                opacity: e.opacity ?? 1,
                diffuseColor: e.diffuseColor || '',
                emissiveColor: e.emissiveColor || '',
                ambientColor: e.ambientColor || '',
            };
        });
        setForms(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id, extractedSignature]);

    useEffect(() => {
        if (item.status !== 'running') return undefined;
        const interval = setInterval(() => ingestionStore.refreshCurrentItem(item.id), 2500);
        return () => clearInterval(interval);
    }, [item.status, item.id]);

    const categoryOptions = useMemo(() => {
        const byId = new Map(categories.map((c) => [c.id, c]));
        const parentIds = new Set(categories.map((c) => c.parentId).filter(Boolean));
        return categories
            .filter((c) => !parentIds.has(c.id))
            .map((c) => {
                const parent = c.parentId ? byId.get(c.parentId) : null;
                return { value: c.id, label: parent ? `${parent.title} > ${c.title}` : c.title };
            });
    }, [categories]);

    const markDirty = () => {
        dirtyRef.current = true;
    };
    const setItemField = (key, value) => {
        markDirty();
        setItemForm((f) => ({ ...f, [key]: value }));
    };
    const setTierField = (key, field, value) => {
        markDirty();
        setTierForms((t) => {
            const next = { ...t[key], [field]: value };
            // Unlockable is priced from the real item price on the shared curve — auto-fill on select.
            if (field === 'accessTier' && value === 'unlockable') {
                next.coinPrice = coinPriceFor(next.scrapedPrice) ?? next.coinPrice ?? null;
            }
            return { ...t, [key]: next };
        });
    };
    const setField = (variantId, key, value) => {
        markDirty();
        setForms((f) => ({ ...f, [variantId]: { ...f[variantId], [key]: value } }));
    };
    const setVariantResizeAxis = (variantId, axis, enabled) => {
        markDirty();
        setForms((f) => {
            const current = f[variantId]?.resizeAxes || defaultAxes;
            const next = enabled ? [...new Set([...current, axis])] : current.filter((a) => a !== axis);
            return { ...f, [variantId]: { ...f[variantId], resizeAxes: next } };
        });
    };
    const setVariantSize = (variantId, axis, value) => {
        markDirty();
        setForms((f) => ({
            ...f,
            [variantId]: { ...f[variantId], size: { ...(f[variantId]?.size || {}), [axis]: value ?? undefined } },
        }));
    };

    const handleRun = async () => {
        try {
            await ingestionStore.runStep(item.id, 3);
            message.success('Extraction queued');
            await ingestionStore.refreshCurrentItem(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to start extraction');
        }
    };

    const handleSave = async () => {
        // Resize uses ONE calibrated axis (others follow the model's own
        // proportions) or all three — exactly two is ambiguous, refuse it.
        if (picksResizeAxes) {
            const twoAxes = (item.variants || []).some(
                (v) => ((forms[v.id]?.resizeAxes) || defaultAxes).length === 2,
            );
            if (twoAxes) {
                message.error('Resize must use one axis or all three — two are selected.');
                return;
            }
        }
        setSaving(true);
        try {
            for (const v of item.variants || []) {
                const f = forms[v.id] || {};
                const tier = tierForms[primaryVarKey(v)] || {};
                const patch = {
                    categoryIds: itemForm.categoryIds,
                    priceUnit: itemForm.priceUnit ?? undefined,
                    // "Can be contained" only applies to FloorItems.
                    ...(isFloorItem ? { canBeContained: itemForm.canBeContained } : {}),
                    accessTier: tier.accessTier || undefined,
                    coinPrice:
                        tier.accessTier === 'unlockable' ? (tier.coinPrice ?? coinPriceFor(tier.scrapedPrice)) : null,
                    // Tags are per primary variation — apply the group's tags to each of its variants.
                    tags: tier.tags ?? [],
                    // Facets are per primary variation — the group's values are
                    // written to each of its variants as one `facets` object.
                    facets: {
                        styles: tier.styles ?? [],
                        roomTypes: tier.roomTypes ?? [],
                        materials: tier.materials ?? [],
                        colors: tier.colors ?? [],
                    },
                };
                if (supportsSize) {
                    const size = {};
                    ['width', 'height', 'depth'].forEach((axis) => {
                        if (f.size?.[axis] != null) size[axis] = f.size[axis];
                    });
                    patch.size = Object.keys(size).length ? size : undefined;
                    patch.resizeAxes = f.resizeAxes ?? defaultAxes;
                }
                if (isFlatRug) {
                    // A flat rug's Width/Height are its physical size; round rugs
                    // keep width === height === diameter.
                    patch.uSize = f.uSize ?? undefined;
                    patch.vSize = f.rugShape === 'round' ? (f.uSize ?? undefined) : (f.vSize ?? undefined);
                    patch.rugShape = f.rugShape || 'rect';
                }
                if (isPillow) {
                    patch.allowedType = undefined;
                    patch.specularColor = undefined;
                    patch.repeat = false;
                    patch.uSize = undefined;
                    patch.vSize = undefined;
                    const stated = { width: f.size?.width ?? undefined, height: f.size?.height ?? undefined };
                    patch.size = stated.width != null && stated.height != null ? stated : undefined;
                } else if (isCurtain) {
                    patch.allowedType = undefined;
                    patch.repeat = true;
                    patch.opacity = f.opacity ?? 1;
                    CURTAIN_COLOR_FIELDS.forEach(({ key }) => {
                        patch[key] = stripHash(f[key]) || undefined;
                    });
                    // The pattern repeat is square: one tile size for both axes.
                    patch.uSize = f.uSize ?? undefined;
                    patch.vSize = f.uSize ?? undefined;
                } else if (isTexture) {
                    if (isWallPrint) {
                        // A print's Width/Height are its physical size (from the variation).
                        patch.uSize = f.uSize ?? undefined;
                        patch.vSize = f.vSize ?? undefined;
                        // No "can be dropped on" / specular; a print never tiles.
                        patch.allowedType = undefined;
                        patch.specularColor = undefined;
                        patch.repeat = false;
                    } else {
                        patch.allowedType = f.allowedType || undefined;
                        patch.specularColor = stripHash(f.specularColor) || undefined;
                        patch.repeat = f.repeat;
                        // Only a repeatable swatch has its own tile size; a non-repeating
                        // mural is sized by its container, so clear uSize/vSize.
                        patch.uSize = f.repeat ? (f.uSize ?? undefined) : undefined;
                        patch.vSize = f.repeat ? (f.vSize ?? undefined) : undefined;
                    }
                }
                await ingestionApi.updateExtracted(item.id, v.id, patch);
            }
            message.success('Saved');
            // Edits are persisted — let the refreshed data re-seed the form.
            dirtyRef.current = false;
            await ingestionStore.refreshCurrentItem(item.id);
        } catch (e) {
            message.error(e.message || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const runCutoutAction = async (variantId, action, successMsg, errorMsg) => {
        setCutoutBusy(variantId);
        try {
            await action();
            message.success(successMsg);
            await ingestionStore.refreshCurrentItem(item.id);
        } catch (e) {
            message.error(e.message || errorMsg);
        } finally {
            setCutoutBusy(null);
        }
    };

    const handleReplaceCutout = (variantId, file) =>
        runCutoutAction(variantId, () => ingestionApi.replaceVariantCutout(item.id, variantId, file), 'Cutout replaced', 'Failed to replace cutout');

    const handlePickImage = (variantId, sourceUrl) =>
        runCutoutAction(variantId, () => ingestionApi.setVariantImageFromUrl(item.id, variantId, sourceUrl), 'Image updated', 'Failed to set image');

    const missingFields = useMemo(() => {
        const set = new Set();
        (item.variants || []).forEach((v) => {
            const e = v.extracted || {};
            if (!(e.categoryIds && e.categoryIds.length)) set.add('category');
            if (e.priceUnit == null) set.add('price unit');
            if (supportsSize && !hasUsableSize(e.size)) set.add('size');
            // Wall print width/height come from the size variation — required.
            if (isWallPrint && (e.uSize == null || e.vSize == null)) set.add('size');
            // Flat rug width/height come from the size variation title — required.
            if (isFlatRug && (e.uSize == null || e.vSize == null)) set.add('size');
            // A pillow is built at its fixed width/height — required.
            if (isPillow && (e.size?.width == null || e.size?.height == null)) set.add('size');
            if (isTexture && !isWallPrint && !isCurtain && !isPillow && !e.allowedType) set.add('allowed type');
            if (isCurtain && (e.opacity == null || !e.diffuseColor)) set.add('curtain material');
            if (needsImage && !v.imageId) set.add('image (background removal)');
            // An unlockable variant with no price would ship as a free unlock.
            if (e.accessTier === 'unlockable' && e.coinPrice == null) set.add('coin price');
            // Empty tags/facets usually mean the derivation failed, not that
            // nothing applies — surface them so they get filled by hand.
            if (!(e.tags && e.tags.length)) set.add('tags');
            const facets = e.facets || {};
            if (!(facets.styles && facets.styles.length)) set.add('styles');
            if (!(facets.roomTypes && facets.roomTypes.length)) set.add('room types');
            if (!(facets.materials && facets.materials.length)) set.add('materials');
            if (!(facets.colors && facets.colors.length)) set.add('colors');
        });
        return [...set];
    }, [item.variants, isTexture, isWallPrint, isCurtain, isPillow, isFlatRug, supportsSize, needsImage]);

    // The detail endpoint flags variants that have scraped data. No data → nothing
    // to extract from, so warn and block proceeding until scraping is (re)run.
    const noScrapedData = (item.variants || []).length > 0 && !(item.variants || []).some((v) => v.hasScrapedData);

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card>
                <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                    <Button icon={<ReloadOutlined />} onClick={() => ingestionStore.refreshCurrentItem(item.id)}>
                        Refresh
                    </Button>
                    <Button
                        type="primary"
                        icon={<ExperimentOutlined />}
                        loading={ingestionStore.runningStep}
                        onClick={handleRun}
                    >
                        Run extraction
                    </Button>
                </Space>
                {noScrapedData && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type="error"
                        showIcon
                        message="No scraped data"
                        description="There's nothing to extract from yet. Run scraping (step 2) first — the item can't proceed until it has scraped data."
                    />
                )}
                {!noScrapedData && missingFields.length > 0 && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type="warning"
                        showIcon
                        message={`Needs manual input: ${missingFields.join(', ')}`}
                        description="These couldn't be filled automatically"
                    />
                )}
            </Card>

            <Card title="Item details" size="small">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <div>
                        <Text>Categories</Text>
                        <Select
                            mode="multiple"
                            style={{ width: '100%' }}
                            placeholder="Select categories"
                            value={itemForm.categoryIds}
                            onChange={(v) => setItemField('categoryIds', v)}
                            options={categoryOptions}
                            showSearch
                            optionFilterProp="label"
                            loading={categories.length === 0}
                        />
                    </div>
                    <div>
                        <Text>Price unit</Text>
                        <br />
                        <Select
                            style={{ width: 200 }}
                            placeholder="Select"
                            value={itemForm.priceUnit}
                            onChange={(v) => setItemField('priceUnit', v)}
                            options={PRICE_UNITS}
                        />
                    </div>
                    {isFloorItem && (
                        <div>
                            <Checkbox
                                checked={!!itemForm.canBeContained}
                                onChange={(e) => setItemField('canBeContained', e.target.checked)}
                            >
                                Can be contained
                            </Checkbox>
                        </div>
                    )}
                </Space>
            </Card>

            <Card title="Primary variations" size="small">
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    {tierGroups.map((g) => {
                        const t = tierForms[g.key] || {};
                        const groupUrl = (item.variants || []).find((v) => primaryVarKey(v) === g.key)?.purchaseUrl;
                        return (
                            <div key={g.key} style={{ borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
                                <Space align="start" size="large" style={{ width: '100%' }}>
                                    {needsImage && (
                                        <VariationImagePanel
                                            item={item}
                                            variationId={g.key}
                                            onChanged={() => ingestionStore.refreshCurrentItem(item.id)}
                                        />
                                    )}
                                    <div style={{ width: '100%' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                            <Text strong>{g.label}</Text>
                                            {groupUrl && (
                                                <Button
                                                    size="small"
                                                    icon={<ExportOutlined />}
                                                    href={groupUrl}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    Go to URL
                                                </Button>
                                            )}
                                        </div>
                                        <div style={{ marginTop: 8 }}>
                                            <Space size="large" wrap align="end">
                                                <Select
                                                    style={{ width: 160 }}
                                                    placeholder="Access tier"
                                                    value={t.accessTier}
                                                    onChange={(v) => setTierField(g.key, 'accessTier', v)}
                                                    options={TIER_OPTIONS}
                                                />
                                                {t.accessTier === 'unlockable' && (
                                                    <InputNumber
                                                        addonBefore="Coins"
                                                        style={{ width: 160 }}
                                                        min={0}
                                                        precision={0}
                                                        value={t.coinPrice}
                                                        onChange={(v) => setTierField(g.key, 'coinPrice', v)}
                                                    />
                                                )}
                                            </Space>
                                        </div>
                                        <div style={{ marginTop: 8 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Tags
                                            </Text>
                                            <Select
                                                mode="tags"
                                                style={{ width: '100%' }}
                                                placeholder="Add tags"
                                                value={t.tags || []}
                                                onChange={(v) => setTierField(g.key, 'tags', v)}
                                                notFoundContent={null}
                                                suffixIcon={null}
                                            />
                                        </div>
                                        {facetVocab && (
                                            <>
                                                <WeightedFacetEditor
                                                    label="Styles"
                                                    options={vocabOptions(facetVocab.styles)}
                                                    value={t.styles || []}
                                                    onChange={(v) => setTierField(g.key, 'styles', v)}
                                                />
                                                <WeightedFacetEditor
                                                    label="Room types"
                                                    options={vocabOptions(facetVocab.roomTypes)}
                                                    value={t.roomTypes || []}
                                                    onChange={(v) => setTierField(g.key, 'roomTypes', v)}
                                                />
                                                <WeightedFacetEditor
                                                    label="Materials"
                                                    options={vocabOptions(facetVocab.materials)}
                                                    value={t.materials || []}
                                                    onChange={(v) => setTierField(g.key, 'materials', v)}
                                                />
                                                <WeightedFacetEditor
                                                    label="Colors (shade + share of the product, 0–1)"
                                                    options={vocabOptions(facetVocab.colorShades)}
                                                    value={t.colors || []}
                                                    onChange={(v) => setTierField(g.key, 'colors', v)}
                                                    defaultWeight={0.5}
                                                />
                                            </>
                                        )}
                                    </div>
                                </Space>
                            </div>
                        );
                    })}
                </Space>
            </Card>

            {sortedVariants(item).map((variant) => {
                const form = forms[variant.id] || { tags: [], size: {} };
                return (
                    <Card key={variant.id} size="small" title={variationLabel(item, variant)}>
                        <Space align="start" size="large" style={{ width: '100%' }}>
                            {needsImage && (
                                <div style={{ width: 96, flexShrink: 0 }}>
                                    <div
                                        style={{
                                            width: 96,
                                            height: 96,
                                            borderRadius: 6,
                                            background: '#fafafa',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {variant.imageId ? (
                                            <img
                                                src={catalogImageUrl(variant.imageId, 96)}
                                                alt="variant thumbnail"
                                                style={{ maxWidth: '100%', maxHeight: '100%' }}
                                            />
                                        ) : (
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                no image
                                            </Text>
                                        )}
                                    </div>
                                    <Button
                                        icon={<UploadOutlined />}
                                        size="small"
                                        block
                                        style={{ marginTop: 6 }}
                                        loading={cutoutBusy === variant.id}
                                        onClick={() => setReplaceVariantId(variant.id)}
                                    >
                                        Replace
                                    </Button>
                                    {variant.imageId && (
                                        <Button
                                            icon={<ExpandOutlined />}
                                            size="small"
                                            block
                                            style={{ marginTop: 6 }}
                                            disabled={cutoutBusy === variant.id}
                                            onClick={() => setCropVariantId(variant.id)}
                                        >
                                            Crop
                                        </Button>
                                    )}
                                </div>
                            )}
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                {supportsSize && (
                                    <div>
                                        <Text>Size (inches)</Text>
                                        <br />
                                        <Space wrap align="start">
                                            {[
                                                { key: 'width', label: 'W' },
                                                { key: 'height', label: 'H' },
                                                { key: 'depth', label: 'D' },
                                            ].map(({ key, label }) => (
                                                <Space key={key} direction="vertical" size={2}>
                                                    <InputNumber
                                                        step={0.1}
                                                        placeholder={key}
                                                        addonBefore={label}
                                                        style={{ width: 130 }}
                                                        value={axisNumber(form.size?.[key])}
                                                        onChange={(v) => setVariantSize(variant.id, key, v)}
                                                    />
                                                    {picksResizeAxes && (
                                                        <Checkbox
                                                            checked={(form.resizeAxes || defaultAxes).includes(key)}
                                                            onChange={(e) => setVariantResizeAxis(variant.id, key, e.target.checked)}
                                                        >
                                                            <Text type="secondary" style={{ fontSize: 12 }}>use for resize</Text>
                                                        </Checkbox>
                                                    )}
                                                </Space>
                                            ))}
                                        </Space>
                                        {picksResizeAxes && (form.resizeAxes || defaultAxes).length === 2 && (
                                            <div>
                                                <Text type="danger" style={{ fontSize: 12 }}>
                                                    Resize must use one axis or all three.
                                                </Text>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {isFlatRug && (
                                    <Space size="large" wrap align="end">
                                        <div>
                                            <Text>Shape</Text>
                                            <br />
                                            <Select
                                                style={{ width: 150 }}
                                                value={form.rugShape || 'rect'}
                                                onChange={(v) => setField(variant.id, 'rugShape', v)}
                                                options={[
                                                    { value: 'rect', label: 'Rectangular' },
                                                    { value: 'round', label: 'Round' },
                                                ]}
                                            />
                                        </div>
                                        <div>
                                            <Text>{form.rugShape === 'round' ? 'Diameter (in)' : 'Width (in)'}</Text>
                                            <br />
                                            <InputNumber
                                                step={0.1}
                                                value={form.uSize}
                                                onChange={(v) => setField(variant.id, 'uSize', v)}
                                            />
                                        </div>
                                        {form.rugShape !== 'round' && (
                                            <div>
                                                <Text>Depth (in)</Text>
                                                <br />
                                                <InputNumber
                                                    step={0.1}
                                                    value={form.vSize}
                                                    onChange={(v) => setField(variant.id, 'vSize', v)}
                                                />
                                            </div>
                                        )}
                                    </Space>
                                )}

                                {isPillow && (
                                    <div>
                                        <Text>Size (inches)</Text>
                                        <br />
                                        <Space wrap>
                                            {[
                                                { key: 'width', label: 'W' },
                                                { key: 'height', label: 'H' },
                                            ].map(({ key, label }) => (
                                                <InputNumber
                                                    key={key}
                                                    step={0.5}
                                                    placeholder={key}
                                                    addonBefore={label}
                                                    style={{ width: 130 }}
                                                    value={axisNumber(form.size?.[key])}
                                                    onChange={(v) => setVariantSize(variant.id, key, v)}
                                                />
                                            ))}
                                        </Space>
                                    </div>
                                )}

                                {isCurtain && (
                                    <Space size="large" wrap align="end">
                                        <div>
                                            <Text>Opacity (1 blackout, 0.5 sheer)</Text>
                                            <br />
                                            <InputNumber
                                                min={0.2}
                                                max={1}
                                                step={0.05}
                                                value={form.opacity}
                                                onChange={(v) => setField(variant.id, 'opacity', v)}
                                            />
                                        </div>
                                        {CURTAIN_COLOR_FIELDS.map(({ key, label, fallback }) => (
                                            <div key={key}>
                                                <Text>{label}</Text>
                                                <br />
                                                <ColorPicker
                                                    value={form[key] ? `#${stripHash(form[key])}` : fallback}
                                                    onChange={(c) => setField(variant.id, key, stripHash(c.toHexString()))}
                                                    showText
                                                />
                                            </div>
                                        ))}
                                        <div>
                                            <Text>Pattern repeat (in)</Text>
                                            <br />
                                            <InputNumber
                                                min={0.5}
                                                step={0.5}
                                                value={form.uSize}
                                                onChange={(v) => setField(variant.id, 'uSize', v)}
                                            />
                                        </div>
                                    </Space>
                                )}

                                {isTexture && !isCurtain && !isPillow && (
                                    <Space size="large" wrap align="end">
                                        {!isWallPrint && (
                                            <div>
                                                <Text>Can be dropped on</Text>
                                                <br />
                                                <Select
                                                    style={{ width: 200 }}
                                                    placeholder="Select"
                                                    value={form.allowedType}
                                                    onChange={(v) => setField(variant.id, 'allowedType', v)}
                                                    options={ALLOWED_TYPES}
                                                />
                                            </div>
                                        )}
                                        {!isWallPrint && (
                                            <div>
                                                <Text>Specular (shine)</Text>
                                                <br />
                                                <ColorPicker
                                                    value={form.specularColor ? `#${stripHash(form.specularColor)}` : '#000000'}
                                                    onChange={(c) =>
                                                        setField(variant.id, 'specularColor', stripHash(c.toHexString()))
                                                    }
                                                    showText
                                                />
                                            </div>
                                        )}
                                        {/* A mural is sized by its container — Width/Height
                                            apply only to a wall print or a repeatable swatch. */}
                                        {(isWallPrint || form.repeat) && (
                                            <>
                                                <div>
                                                    <Text>Width (in)</Text>
                                                    <br />
                                                    <InputNumber
                                                        step={0.1}
                                                        value={form.uSize}
                                                        onChange={(v) => setField(variant.id, 'uSize', v)}
                                                    />
                                                </div>
                                                <div>
                                                    <Text>Height (in)</Text>
                                                    <br />
                                                    <InputNumber
                                                        step={0.1}
                                                        value={form.vSize}
                                                        onChange={(v) => setField(variant.id, 'vSize', v)}
                                                    />
                                                </div>
                                            </>
                                        )}
                                        {!isWallPrint && (
                                            <Checkbox
                                                checked={form.repeat}
                                                onChange={(e) => setField(variant.id, 'repeat', e.target.checked)}
                                            >
                                                Repeats (tileable)
                                            </Checkbox>
                                        )}
                                    </Space>
                                )}

                                <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10 }}>
                                    <ExternalDataView data={variant.extracted?.externalData} />
                                </div>
                            </Space>
                        </Space>
                    </Card>
                );
            })}

            <Button
                type="primary"
                size="large"
                loading={saving}
                disabled={noScrapedData}
                onClick={handleSave}
            >
                Save
            </Button>

            <CropDialog
                open={!!cropVariantId}
                imageUrl={
                    cropVariantId
                        ? catalogImageUrl(
                              (item.variants || []).find((v) => v.id === cropVariantId)?.imageId,
                              1000,
                          )
                        : null
                }
                onCancel={() => setCropVariantId(null)}
                onCropped={async (file) => {
                    const variantId = cropVariantId;
                    await handleReplaceCutout(variantId, file);
                    setCropVariantId(null);
                }}
            />

            <ReplaceImageDialog
                open={!!replaceVariantId}
                itemId={item.id}
                busy={cutoutBusy === replaceVariantId}
                onCancel={() => setReplaceVariantId(null)}
                onUploadFile={async (file) => {
                    await handleReplaceCutout(replaceVariantId, file);
                    setReplaceVariantId(null);
                }}
                onPickUrl={async (url) => {
                    await handlePickImage(replaceVariantId, url);
                    setReplaceVariantId(null);
                }}
            />
        </Space>
    );
});
